import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { debouncerByKey } from 'lib/throttle';

const Wrap = styled.div<{ $zeroWidth: boolean }>`
  position: absolute;
  top: calc(50% - 5px);
  width: 100%;
  padding: 0 4px;
  line-height: 0;
  transition: opacity 0.3s ease-in-out;
  opacity: ${({ $zeroWidth }) => ($zeroWidth ? 0.6 : 1)};
  overflow: hidden;
`;

interface DynamicStripesProps {
  height?: number;
  tickWidth?: number;
  tickSpacing?: number;
  className?: string;
}

const debouncer = debouncerByKey();

export default function SliderDynamicStripes({
  height = 10,
  tickWidth = 1,
  tickSpacing = 4,
}: DynamicStripesProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!width && containerRef.current) {
      // Quick initial update
      setWidth(containerRef.current.offsetWidth);
    }
  }, [width]);

  // TODO: move to new useResizeWithDelay function ?
  useEffect(() => {
    const abortController = new AbortController();
    const abortSignal = abortController.signal;
    const updateWidth = () => {
      if (!containerRef.current || abortSignal.aborted) {
        return;
      }
      debouncer('SliderDynamicStripes', 1000, () => {
        if (!containerRef.current || abortSignal.aborted) {
          return;
        }
        setWidth(containerRef.current.offsetWidth);
      });
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => {
      abortController.abort();
      window.removeEventListener('resize', updateWidth);
    };
  }, [width]);

  /** Fallback to 300 with overflow hidden until we know width */
  const numberOfTicks = width ? Math.ceil(width / tickSpacing) : 300;

  return (
    <Wrap ref={containerRef} $zeroWidth={width === 0}>
      <svg width="100%" height={height} className="bg-black">
        <defs>
          <linearGradient
            id="sliderStripeId"
            x1="0"
            y1="5"
            x2={width}
            y2="5"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="var(--main)" stopOpacity="0.15" />
            <stop offset="1" stopColor="var(--main)" />
          </linearGradient>
        </defs>
        <g>
          {Array.from({ length: numberOfTicks }).map((_, index) => (
            <rect
              key={index}
              x={index * tickSpacing}
              y={0}
              width={tickWidth}
              height={height}
              fill="url(#sliderStripeId)"
            />
          ))}
        </g>
      </svg>
    </Wrap>
  );
}
